/* eslint-disable react-hooks/exhaustive-deps */
import { FormEventHandler, useEffect, useState } from 'react';

import { useRouter } from 'next/router';
import { useSearchParams } from 'next/navigation';

import { DefaultTheme, withTheme } from 'styled-components';

import { getUserSubscriptionType } from '~/common/utils/get-user-subscription-type';
import { getURl } from '~/common/services/fetch';
import { setAuthToken } from '~/common/services/customAxios';
import { loginEmailPassword } from '~/common/services/controllers/auth';
import { useToast } from '~/common/providers/toast';
import { useAuth } from '~/common/providers/auth';
import { SPACING } from '~/common/layout-propertis/spacing';
import { useCookies } from '~/common/hooks/use-cookies';
import { cookies } from '~/common/constants/cookies';
import { Typography } from '~/common/components/Typography';
import { PublicPage } from '~/common/components/Page/PublicPage';
import { Icon } from '~/common/components/Icons';
import { TextInput } from '~/common/components/Form/TextInput';
import { Button } from '~/common/components/Button';

import {
  AppAuthDataInfoContainer,
  StyContainer,
  StyCreateAccount,
  StyOpenAccount,
} from './styles';

interface ILoginPage {
  theme?: DefaultTheme;
}

export const LoginPage: React.FC<ILoginPage> = withTheme(({ theme }) => {
  const { showToast } = useToast();
  const { setItem } = useCookies();
  const { loadUser, assignTempUser } = useAuth();
  const router = useRouter();
  const searchParams = useSearchParams();
  const [loadingDefaultLogin, setLoadingDefaultLogin] =
    useState<boolean>(false);
  const [invalidEmail, setInvalidEmail] = useState<boolean>(false);
  const [invalidPassword, setInvalidPassword] = useState<boolean>(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [userPassword, setUserPassword] = useState<string | null>(null);
  const [emailType, setEmailType] = useState<string>('email');
  const isValidEmail = (email: string): boolean => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const handleLoadUser = async (): Promise<void> => {
    await loadUser().then(() => {
      const queryParams = window.location.search
        .replace('?', '')
        .split('&')
        .reduce((acc, param) => {
          const [key, value] = param.split('=');
          if (
            key &&
            value &&
            !['oauth_success', 'authtoken'].includes(key.toLowerCase())
          ) {
            acc[key] = value;
          }
          return acc;
        }, {} as Record<string, string>);

      const returnUrl = queryParams.returnUrl;
      delete queryParams.returnUrl;

      const queryParamsString = new URLSearchParams(queryParams).toString();

      router.push({
        pathname: returnUrl ? decodeURIComponent(returnUrl) : '/portfolio',
        query: queryParamsString.replace('?', ''),
      });
    });
  };

  const handleLoginEmailPassword = async (): Promise<void> => {
    setLoadingDefaultLogin(true);
    try {
      const pushToken = localStorage?.getItem('PushToken');
      const pushTokenOS = localStorage?.getItem('PushTokenOS');
      const response = await loginEmailPassword({
        login: userEmail,
        senha: userPassword,
        pushToken,
        pushTokenOS,
      });

      const { accessToken, usuario } = response.data;
      setItem(cookies.AUTH_TOKEN, accessToken);
      setAuthToken(accessToken);

      assignTempUser({
        id: String(usuario.id),
        nome: usuario.email,
        email: usuario.email,
        userType: getUserSubscriptionType(usuario.userType),
      });

      await handleLoadUser();
    } catch (error) {
      showToast({
        title: 'E-mail ou senha incorretos.',
        type: 'error',
        position: 'top-right',
        duration: 6500,
        description:
          'Em caso de dúvidas, contate nosso suporte no e-mail contact@cryptocontrol.pro',
      });
    } finally {
      setLoadingDefaultLogin(false);
    }
  };

  const handleLoginGoogle = async (): Promise<void> => {
    try {
      const googleRedirect = `https://accounts.google.com/o/oauth2/v2/auth/oauthchooseaccount?response_type=code&redirect_uri=${encodeURIComponent(
        `${getURl('cryptocontrol')}Autenticacao/oauth/google`
      )}&scope=profile%20email&state=${encodeURIComponent(
        window.location.origin
      )}&client_id=71029812611-97vf77cqvpgegn9prp6uv3q9mtiorrpb.apps.googleusercontent.com&service=lso&o2v=2&ddm=1&flowName=GeneralOAuthFlow&response_mode=form_post`;
      window.location.href = googleRedirect;
    } catch (error) {
      showToast({
        title: 'Erro ao logar',
        type: 'error',
        position: 'top-right',
        duration: 6500,
        description: 'Não foi possível realizar login com o google',
      });
    }
  };

  const handleLoginApple = async (): Promise<void> => {
    try {
      const googleRedirect = `https://appleid.apple.com/auth/authorize?client_id=cryptocontrol&redirect_uri=${encodeURIComponent(
        `${getURl('cryptocontrol')}Autenticacao/oauth/apple`
      )}&response_type=code%20id_token&scope=name%20email&response_mode=form_post&state=${encodeURIComponent(
        window.location.origin
      )}`;
      window.location.href = googleRedirect;
    } catch (error) {
      showToast({
        title: 'Erro ao logar',
        type: 'error',
        position: 'top-right',
        duration: 6500,
        description: 'Não foi possível realizar login com a apple',
      });
    }
  };

  const handleSubmit: FormEventHandler = (event) => {
    event.preventDefault();
    if ((userEmail === 'admin' || isValidEmail(userEmail)) && userPassword) {
      return handleLoginEmailPassword();
    }
    if (userEmail === 'admin' || isValidEmail(userEmail)) {
      setInvalidEmail(false);
    } else {
      setInvalidEmail(true);
    }
    if (userPassword) {
      setInvalidPassword(false);
    } else {
      setInvalidPassword(true);
    }
  };

  useEffect(() => {
    // login via google ou apple
    const oauth_success = searchParams.get('oauth_success');
    const authToken = searchParams.get('authToken');
    if (oauth_success && oauth_success === 'true' && authToken) {
      setItem(cookies.AUTH_TOKEN, authToken);
      setAuthToken(authToken);
      handleLoadUser();
    }
    if (oauth_success && oauth_success === 'false') {
      showToast({
        title: 'Erro ao logar',
        type: 'error',
        position: 'top-right',
        duration: 5000,
        description: 'Não foi possível realizar o login',
      });
    }
  }, [handleLoadUser, searchParams, setItem, showToast]);

  return (
    <>
      <span
        className="login-auth-form"
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: `${SPACING._24}`,
        }}
      >
        <PublicPage title="Login">
          <StyContainer onSubmit={handleSubmit}>
            <TextInput
              type={emailType}
              name="input1-x-small"
              size="large"
              width="100%"
              style={{ width: '100%' }}
              leftIcon="mail-line"
              label={{
                label: 'E-mail',
                isRequired: true,
              }}
              value={userEmail}
              error={invalidEmail && 'Insira um e-mail válido'}
              onChange={(e) => {
                if (e.target.value === 'admin') {
                  setEmailType('text');
                } else {
                  setEmailType('email');
                }
                setUserEmail(e.target.value);
                setInvalidEmail(false);
              }}
              placeholder="Ex.: email@user.com"
              autoComplete="off"
            />
            <TextInput
              type="password"
              name="input1-x-small"
              size="large"
              width="100%"
              style={{ width: '100%' }}
              label={{
                label: 'Senha',
                isRequired: true,
              }}
              value={userPassword}
              error={invalidPassword && 'Insira sua senha'}
              onChange={(e) => {
                setUserPassword(e.target.value);
                setInvalidPassword(false);
              }}
              placeholder="Digite sua senha"
              autoComplete="off"
            />
            <StyCreateAccount href="/forgot-password" passHref>
              <Typography.Label size="small" color="neutral.0">
                Esqueceu sua senha?
              </Typography.Label>
            </StyCreateAccount>
            <Button
              type="submit"
              text="Continuar"
              width="100%"
              backgroundColor={theme.green[500]}
              textOrIconColorOnHover={theme.green[500]}
              withArrowRight
              loading={loadingDefaultLogin}
              loader="bar"
              loaderColor="green.500"
            />
            <StyOpenAccount>
              <Typography.Label size="x-small">
                Ainda não tem uma conta?
              </Typography.Label>
              <StyCreateAccount href="/signup" passHref>
                <Typography.Label size="small" color="green.500">
                  Cadastre-se
                </Typography.Label>
                <Icon
                  type="arrow-right-s-line"
                  color={theme.green[500]}
                  size={20}
                />
              </StyCreateAccount>
            </StyOpenAccount>
          </StyContainer>
        </PublicPage>
      </span>
      <AppAuthDataInfoContainer className="app-auth-data-info">
        <Typography.Label size="small" color="neutral.0">
          Autenticando...
        </Typography.Label>
      </AppAuthDataInfoContainer>
    </>
  );
});
